import {
  CLUSTER_STATUSES,
  COMPUTE_CLUSTER_CREATE_TASK,
  COMPUTE_CLUSTER_DESTROY_TASK,
  COMPUTE_CLUSTER_UPDATE_TASK
} from '@/common/constants/computeCluster.constants';
import COMPUTE_ACTIONS_MAP from '@/common/store/modules/compute/compute.notificationsMap';
import { checkIsInActionsMap, tryToDispatchOperationAction } from '@/common/utils/actionMapsMethods';
import { findMutationsByName } from '@/common/utils/store.util';
import storeModulesGroups from '@/common/store/modules/storeModulesGroups';
import useAuthStore from '@/common/store/pinia/auth';
import useComputeVolumesStore from '@/common/store/pinia/computeVolumes';

export default {
  FETCH_COMPUTE_CLUSTER({ commit }, payload = {}) {
    const { params, options } = payload;
    commit('COMPUTE_CLUSTER_SET_REQUEST_STATUS', 'pending');
    return this.$api.get('/api/v2/compute/cluster', params, options)
      .then(async (res) => {
        commit('SET_COMPUTE_CLUSTER', res.data);
        commit('COMPUTE_CLUSTER_SET_REQUEST_STATUS', 'loaded');
        return res;
      })
      .catch((err) => {
        commit('CLEAR_COMPUTE_CLUSTER');
        commit('COMPUTE_CLUSTER_SET_REQUEST_STATUS', 'loaded');
        throw err;
      });
  },

  async CHECK_COMPUTE_IS_CONFIGURED({ state, getters }) {
    if (state.requestStatus !== 'loaded') {
      await this.$takeFirst('compute/FETCH_COMPUTE_CLUSTER').catch(() => null);
    }

    return getters.isComputeConfigured;
  },

  INSTALL_COMPUTE_CLUSTER({ commit }, body) {
    commit('COMPUTE_CLUSTER_SET_REQUEST_STATUS', 'pending');

    return this.$api.post('/api/v2/compute/cluster', { event: { name: COMPUTE_CLUSTER_CREATE_TASK }, ...body })
      .then(async (res) => {
        commit('SET_COMPUTE_CLUSTER', {
          ...res.data,
          status: CLUSTER_STATUSES.DEPLOYING
        });
        commit('COMPUTE_CLUSTER_SET_REQUEST_STATUS', 'loaded');
        return res;
      })
      .catch((err) => {
        commit('COMPUTE_CLUSTER_SET_REQUEST_STATUS', 'loaded');
        throw err;
      });
  },

  REMOVE_COMPUTE_CLUSTER({ commit }) {
    commit('COMPUTE_CLUSTER_SET_REQUEST_STATUS', 'pending');

    return this.$api.delete('/api/v2/compute/cluster', { event: { name: COMPUTE_CLUSTER_DESTROY_TASK } })
      .then((res) => {
        commit('SET_COMPUTE_CLUSTER', {
          ...res.data,
          status: CLUSTER_STATUSES.DESTROYING
        });
        commit('COMPUTE_CLUSTER_SET_REQUEST_STATUS', 'loaded');
        return res;
      });
  },

  UPDATE_COMPUTE_CLUSTER({ commit }, body) {
    return this.$api.patch('/api/v2/compute/cluster', { event: { name: COMPUTE_CLUSTER_UPDATE_TASK }, ...body })
      .catch((error) => {
        commit('SET_ERRORS', error);

        throw error;
      });
  },

  FETCH_COMPUTE_STATS({ commit }, payload = {}) {
    const { params, options } = payload;
    commit('CLUSTER_STAT_PENDING', true);
    return this.$api.get('/api/v2/compute/cluster/stat', params, options)
      .then((res) => {
        commit('SET_COMPUTE_STATS', res.data);
        commit('CLUSTER_STAT_PENDING', false);
      })
      .catch((e) => {
        commit('CLUSTER_STAT_PENDING', false);
        throw e;
      });
  },

  FETCH_COMPUTE_QUOTAS({ commit }) {
    const authStore = useAuthStore();
    const projectId = authStore.currentProject;
    commit('CLUSTER_STAT_PENDING', true);
    return this.$api.get(`/api/v2/compute/quotas/${projectId}/?usage=True`)
      .then((res) => {
        commit('SET_COMPUTE_QUOTAS', res.data);
        commit('CLUSTER_STAT_PENDING', false);
      })
      .catch((e) => {
        commit('CLUSTER_STAT_PENDING', false);
        throw e;
      });
  },

  FLUSH_COMPUTE({ commit }) {
    const computeVolumesStore = useComputeVolumesStore();
    computeVolumesStore.$reset();
    storeModulesGroups.compute.flatMap(name => findMutationsByName(name, 'FLUSH_STATE'))
      .forEach((mutation) => commit(mutation, null, { root: true }));
  },

  socket_compute(store, event) {
    const { dispatch, commit, rootState } = store;
    const { $can } = this;

    const errorMessage = checkIsInActionsMap({ state: rootState, event, actionsMap: COMPUTE_ACTIONS_MAP, $can });

    if (errorMessage) {
      commit('errors/ADD_ERROR', { message: errorMessage, data: event }, { root: true });

      return tryToDispatchOperationAction({ event, state: rootState, dispatch, commit })
        .catch(e => commit('errors/ADD_ERROR', { message: e, data: event }, { root: true }));
    }

    const { action = 'dispatch', actionName, payloadFormatter = it => it } = COMPUTE_ACTIONS_MAP[event.type][event.operation];

    if (event.event === 'updated' && event.object.status === 'deleting') return Promise.resolve(); // don't fetch, because item is already deleted

    return this[action](
      actionName,
      payloadFormatter(event.object),
      action === 'dispatch' ? { root: true } : undefined
    );
  },

  socket_tasks({ dispatch }, { event }) {
    if (event?.name === COMPUTE_CLUSTER_UPDATE_TASK) {
      dispatch('FETCH_COMPUTE_CLUSTER');
    }
  },
};
